import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Box, Center, Flex } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import Card from 'components/_core/Card';
import Heading from 'components/_core/Heading';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import * as OffersLayout from 'layouts/OffersLayout';

import CollectionCard, {
  CollectionCardLink,
} from '../../components/CollectionCard';
import IntegrationCard from '../../components/IntegrationCard';
import OfferCard from '../../components/OfferCard';
import useGetToolboxCollections from '../../hooks/useGetToolboxCollections';
import useGetToolboxCollectionsCount from '../../hooks/useGetToolboxCollectionsCount';
import useGetToolboxIntegrations from '../../hooks/useGetToolboxIntegrations';
import useGetToolboxIntegrationsCount from '../../hooks/useGetToolboxIntegrationsCount';
import useGetToolboxOffers from '../../hooks/useGetToolboxOffers';
import useGetToolboxOffersCount from '../../hooks/useGetToolboxOffersCount';
import * as routes from '../../routes';
import useGetToolboxHighlightedOffers from './hooks/useGetToolboxHighlightedOffers';
import locales from './locales';

const OFFERS_COUNT = 6;
const COLLECTIONS_COUNT = 3;
const INTEGRATIONS_COUNT = 3;

const Home: FC = () => {
  const offers = useGetToolboxOffers({
    filters: { isHighlighted: false },
    first: OFFERS_COUNT,
  });

  const { count: offersCount } = useGetToolboxOffersCount();

  const collections = useGetToolboxCollections({ first: COLLECTIONS_COUNT });
  const { count: collectionsCount } = useGetToolboxCollectionsCount();

  const integrations = useGetToolboxIntegrations({ first: INTEGRATIONS_COUNT });
  const { count: integrationsCount } = useGetToolboxIntegrationsCount();

  const highlightedOffers = useGetToolboxHighlightedOffers();

  if (
    collections.loading ||
    highlightedOffers.loading ||
    integrations.loading ||
    offers.loading
  ) {
    return <Loader />;
  }

  if (
    collections.error ||
    highlightedOffers.loading ||
    integrations.error ||
    offers.error
  ) {
    return (
      <Center height="100%">
        <ErrorCard
          retry={() =>
            Promise.all([
              collections.refetch(),
              highlightedOffers.refetch(),
              integrations.refetch(),
              offers.refetch(),
            ])
          }
        />
      </Center>
    );
  }

  return (
    <OffersLayout.PageContainer
      trackViewProps={{
        pageName: 'ToolboxHome Viewed',
        usePrefix: false,
      }}
    >
      <OffersLayout.PageTitle
        description={locales.description}
        title={locales.title}
      />

      {highlightedOffers.items ? (
        <Card
          backgroundColor="grey.100"
          borderColor="border"
          marginBottom="space-40"
          padding="space-24"
        >
          <Flex alignItems="center" paddingBottom="space-16">
            <SquircleIcon
              colorScheme="orange"
              marginRight="space-16"
              name="heart"
            />

            <Box>
              <Heading>{locales.highlightedOffers}</Heading>
              <Text size="lg">{locales.highlightedOffersCatchline}</Text>
            </Box>
          </Flex>

          <OffersLayout.ListGrid paddingBottom="0">
            {highlightedOffers.items.map((offer) => (
              <OfferCard.Link
                key={offer.toolboxOfferId}
                to={{
                  pathname: routes.getOfferPath(offer.toolboxOfferId),
                  state: { from: 'home-highlighted-offers' },
                }}
              >
                <OfferCard partner={offer.partner} perkBadge={offer.perkBadge}>
                  {offer.title}
                </OfferCard>
              </OfferCard.Link>
            ))}
          </OffersLayout.ListGrid>
        </Card>
      ) : null}

      <OffersLayout.ListTitle
        count={offersCount}
        justifyContent="space-between"
        title={locales.offers}
      >
        <Button as={Link} to={routes.offers} variant="inline-primary">
          {locales.allOffers}
        </Button>
      </OffersLayout.ListTitle>

      {offers.items ? (
        <OffersLayout.ListGrid>
          {offers.items.map((offer) => (
            <OfferCard.Link
              key={offer.toolboxOfferId}
              to={{
                pathname: routes.getOfferPath(offer.toolboxOfferId),
                state: { from: 'home' },
              }}
            >
              <OfferCard partner={offer.partner} perkBadge={offer.perkBadge}>
                {offer.title}
              </OfferCard>
            </OfferCard.Link>
          ))}
        </OffersLayout.ListGrid>
      ) : null}

      <OffersLayout.ListTitle
        count={integrationsCount}
        justifyContent="space-between"
        title={locales.integrations}
      >
        <Button as={Link} to={routes.integrations} variant="inline-primary">
          {locales.allIntegrations}
        </Button>
      </OffersLayout.ListTitle>

      {integrations.items ? (
        <OffersLayout.ListGrid>
          {integrations.items.map((integration) => (
            <IntegrationCard.Link
              key={integration.toolboxIntegrationId}
              to={{
                pathname: routes.getIntegrationPath(
                  integration.toolboxIntegrationId,
                ),
                state: { from: 'home' },
              }}
            >
              <IntegrationCard partner={integration.partner}>
                {integration.description}
              </IntegrationCard>
            </IntegrationCard.Link>
          ))}
        </OffersLayout.ListGrid>
      ) : null}

      <OffersLayout.ListTitle
        count={collectionsCount}
        justifyContent="space-between"
        title={locales.collections}
      >
        <Button as={Link} to={routes.collections} variant="inline-primary">
          {locales.allCollections}
        </Button>
      </OffersLayout.ListTitle>

      {collections.items ? (
        <OffersLayout.ListGrid>
          {collections.items.map((collection) => (
            <CollectionCardLink
              key={collection.toolboxCollectionId}
              to={{
                pathname: routes.getCollectionPath(
                  collection.toolboxCollectionId,
                ),
                state: { from: 'home' },
              }}
            >
              <CollectionCard collection={collection} />
            </CollectionCardLink>
          ))}
        </OffersLayout.ListGrid>
      ) : null}
    </OffersLayout.PageContainer>
  );
};

export default Home;
